/**
 * portalForUser — portal auto-routing.
 *
 * Given an authenticated user, return the destination the login flow
 * (and any legacy-URL redirect) should send them to.
 *
 * `options` is retained only for legacy/debugging code paths — the M3
 * closure removed the peer-navigation portal switcher, so the UI does
 * not render `options` anywhere. Each authenticated actor now lands
 * inside a single surface appropriate to their role; cross-surface
 * access, when legitimately authorized, happens via deliberate deep
 * links (URL routes still guarded by InternalOnlyRoute / TenantRoute /
 * DeveloperRoute / DemoRoute).
 *
 * Rules match backend guards exactly:
 *   INTERNAL_ROLES → /admin  (deep-link access to /institution + /developer + /demo-portal still permitted)
 *   institution_admin + institution_id → /institution (deep-link to /developer permitted via DeveloperRoute)
 *   compliance_officer → /developer (developer:read)
 *   citizen → mobile app (web fallback: /demo-portal)
 *   others → /demo-portal (harmless sandbox)
 */

const INTERNAL_ROLES = new Set([
  "system_admin",
  "compliance_officer",
  "security_officer",
  "support_officer",
  "auditor",
  "identity_officer",
  "executive_viewer",
]);

// Roles that see the Developer Portal (mirrors backend RBAC:
// developer:read → system_admin, institution_admin, compliance_officer).
const DEVELOPER_ROLES = new Set([
  "system_admin",
  "institution_admin",
  "compliance_officer",
]);

export function portalsForUser(user) {
  if (!user) return { primary: "/login", options: [] };
  const isInternal = INTERNAL_ROLES.has(user.role);
  const isTenant = user.role === "institution_admin" && !!user.institution_id;
  const isCitizen = user.role === "citizen";
  const isDeveloper = DEVELOPER_ROLES.has(user.role);

  const options = [];
  if (isInternal) options.push({ label: "Admin Console", path: "/admin", key: "admin" });
  if (isInternal || isTenant) options.push({ label: "Institution Portal", path: "/institution", key: "institution" });
  if (isDeveloper) options.push({ label: "Developer Portal", path: "/developer", key: "developer" });
  // Demo portal is available to every authenticated session (sandbox-only backend anyway).
  options.push({ label: "Demo Portal", path: "/demo-portal", key: "demo" });

  let primary;
  if (isInternal) primary = "/admin";
  else if (isTenant) primary = "/institution";
  else if (isCitizen) primary = "/demo-portal";
  else primary = "/demo-portal";

  return { primary, options };
}
